/**
 * CoinCreditsPage — super-admin manual coin credit tool.
 *
 * Sub-admins can't reach this page (dependency-blocked at the API layer;
 * route also uses AdminOnlyRoute in App.js).
 *
 * Flow:
 *   1. Enter INDoS number → GET /admin/coin-credits/user/{indos} shows the
 *      target user's identity + current balance so you know who you're
 *      about to credit.
 *   2. Enter amount (1-500) + reason (min 10 chars) → confirmation dialog.
 *   3. POST credit → user's balance updates + audit row written.
 *   4. Recent credits table below shows the history; reversal button
 *      appears for credits within their 24-hour window.
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import Layout from '../../components/layout/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { ArrowLeft, Coins, Search, Loader2, Undo2, AlertTriangle, User as UserIcon, ShieldCheck } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const CoinCreditsPage = () => {
  const navigate = useNavigate();

  // Lookup state
  const [indos, setIndos] = useState('');
  const [target, setTarget] = useState(null);
  const [lookupLoading, setLookupLoading] = useState(false);

  // Credit form state
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // History + filters + pagination
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [reversing, setReversing] = useState(null);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [historyPage, setHistoryPage] = useState(1);
  const HISTORY_PER_PAGE = 25;
  const [filterIndos, setFilterIndos] = useState('');
  const [filterKind, setFilterKind] = useState('all');
  const [appliedFilters, setAppliedFilters] = useState({ indos: '', kind: 'all' });

  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const params = { page: historyPage, per_page: HISTORY_PER_PAGE };
      if (appliedFilters.indos) params.target_indos = appliedFilters.indos;
      const { data } = await axios.get(`${API}/admin/coin-credits`, { params });
      let items = data.items || [];
      if (appliedFilters.kind !== 'all') items = items.filter((r) => r.kind === appliedFilters.kind);
      setHistory(items);
      setHistoryTotal(data.total || 0);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load history');
    } finally {
      setHistoryLoading(false);
    }
  }, [historyPage, appliedFilters]);

  const applyFilters = () => {
    setHistoryPage(1);
    setAppliedFilters({ indos: filterIndos.trim().toUpperCase(), kind: filterKind });
  };
  const resetFilters = () => {
    setFilterIndos(''); setFilterKind('all'); setHistoryPage(1);
    setAppliedFilters({ indos: '', kind: 'all' });
  };
  const historyTotalPages = Math.max(1, Math.ceil(historyTotal / HISTORY_PER_PAGE));

  useEffect(() => { loadHistory(); }, [loadHistory]);

  const lookup = async () => {
    if (!indos.trim()) return;
    setLookupLoading(true);
    setTarget(null);
    try {
      const { data } = await axios.get(`${API}/admin/coin-credits/user/${encodeURIComponent(indos.trim())}`);
      setTarget(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Lookup failed');
    } finally {
      setLookupLoading(false);
    }
  };

  const submitCredit = async () => {
    setSubmitting(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/coin-credits/${encodeURIComponent(target.indos_number)}`,
        { amount: Number(amount), reason: reason.trim() },
      );
      toast.success(`Credited ${amount} coins to ${target.name}. New balance: ${data.balance_after}`);
      setConfirmOpen(false);
      setAmount('');
      setReason('');
      setTarget({ ...target, coins_balance: data.balance_after });
      await loadHistory();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Credit failed');
    } finally {
      setSubmitting(false);
    }
  };

  const doReverse = async (creditId) => {
    if (!window.confirm('Reverse this credit? The user\'s balance will be debited and both actions will remain in the audit log.')) return;
    setReversing(creditId);
    try {
      await axios.post(`${API}/admin/coin-credits/${creditId}/reverse`);
      toast.success('Credit reversed');
      await loadHistory();
      if (target) {
        // Refresh target balance display
        try {
          const { data } = await axios.get(`${API}/admin/coin-credits/user/${encodeURIComponent(target.indos_number)}`);
          setTarget(data);
        } catch (_) { /* noop */ }
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Reversal failed');
    } finally {
      setReversing(null);
    }
  };

  const withinReversalWindow = (createdAt) => {
    if (!createdAt) return false;
    const age = Date.now() - new Date(createdAt).getTime();
    return age < 24 * 60 * 60 * 1000;
  };

  const canSubmit = target && Number(amount) >= 1 && Number(amount) <= 500 && reason.trim().length >= 10;

  return (
    <Layout>
      <div className="page-transition max-w-5xl mx-auto">
        <div className="flex items-center gap-4 mb-6">
          <Button variant="ghost" onClick={() => navigate('/admin/tools')} className="text-gray-600" data-testid="coin-credit-back-btn">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Admin Tools
          </Button>
        </div>

        <Card className="mb-6 border-l-4 border-l-amber-500 shadow-lg">
          <CardHeader className="bg-gradient-to-r from-amber-500/5 to-yellow-500/5">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-amber-500 rounded-lg">
                <Coins className="w-7 h-7 text-white" />
              </div>
              <div>
                <CardTitle className="text-2xl text-amber-700">Manual Coin Credit</CardTitle>
                <CardDescription className="text-sm mt-1 flex items-center gap-2">
                  <ShieldCheck className="w-3.5 h-3.5 text-emerald-600" />
                  Super-admin only. Every credit is audit-logged with your identity, IP and reason. Reversible within 24 hours.
                </CardDescription>
              </div>
            </div>
          </CardHeader>
        </Card>

        {/* Lookup + Credit form */}
        <Card className="mb-6">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Credit a user</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label className="text-xs text-gray-600 mb-1">INDoS number</Label>
              <div className="flex gap-2">
                <Input
                  value={indos}
                  onChange={(e) => setIndos(e.target.value.toUpperCase())}
                  onKeyDown={(e) => { if (e.key === 'Enter') lookup(); }}
                  placeholder="e.g. 24TP1234"
                  className="max-w-xs uppercase"
                  data-testid="coin-credit-indos-input"
                />
                <Button onClick={lookup} disabled={lookupLoading || !indos.trim()} className="bg-amber-600 hover:bg-amber-700" data-testid="coin-credit-lookup-btn">
                  {lookupLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4 mr-2" />}
                  Look up
                </Button>
              </div>
            </div>

            {target && (
              <div className="p-4 bg-gray-50 rounded-lg border" data-testid="coin-credit-target-card">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                    <UserIcon className="w-5 h-5 text-amber-700" />
                  </div>
                  <div>
                    <div className="font-medium text-gray-900">{target.name}</div>
                    <div className="text-xs text-gray-500">{target.email} · INDoS <span className="font-mono">{target.indos_number}</span></div>
                  </div>
                  <div className="ml-auto text-right">
                    <div className="text-xs text-gray-500">Current balance</div>
                    <div className="text-xl font-semibold text-amber-700 tabular-nums flex items-center gap-1">
                      <Coins className="w-4 h-4" /> {target.coins_balance || 0}
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 pt-3 border-t">
                  <div>
                    <Label className="text-xs text-gray-600">Amount (1–500)</Label>
                    <Input
                      type="number" min="1" max="500"
                      value={amount}
                      onChange={(e) => setAmount(e.target.value)}
                      placeholder="e.g. 5"
                      data-testid="coin-credit-amount-input"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <Label className="text-xs text-gray-600">Reason (min 10 chars, required — will be audit-logged)</Label>
                    <Textarea
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                      rows={2}
                      placeholder="e.g. Reward for MEO Class 2 written paper submission via external form"
                      data-testid="coin-credit-reason-input"
                    />
                    <p className={`text-[10px] mt-0.5 ${reason.trim().length >= 10 ? 'text-emerald-600' : 'text-gray-400'}`}>
                      {reason.trim().length}/10 minimum
                    </p>
                  </div>
                </div>

                <div className="mt-4">
                  <Button
                    onClick={() => setConfirmOpen(true)}
                    disabled={!canSubmit}
                    className="bg-amber-600 hover:bg-amber-700 text-white"
                    data-testid="coin-credit-submit-btn"
                  >
                    <Coins className="w-4 h-4 mr-2" />
                    Credit {amount || 0} coins
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Confirmation modal */}
        <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-amber-600" />
                Confirm coin credit
              </DialogTitle>
            </DialogHeader>
            {target && (
              <div className="space-y-3">
                <p className="text-sm text-gray-700">
                  You are about to credit <span className="font-semibold text-amber-700">{amount} coins</span> to:
                </p>
                <div className="p-3 bg-gray-50 rounded border text-sm">
                  <div><span className="text-gray-500">Name:</span> <span className="font-medium">{target.name}</span></div>
                  <div><span className="text-gray-500">Email:</span> {target.email}</div>
                  <div><span className="text-gray-500">INDoS:</span> <span className="font-mono">{target.indos_number}</span></div>
                  <div><span className="text-gray-500">Balance after:</span> <span className="font-semibold text-amber-700">{(target.coins_balance || 0) + Number(amount)}</span></div>
                </div>
                <div className="p-3 bg-amber-50 border border-amber-200 rounded text-xs text-amber-900">
                  <span className="font-semibold">Reason:</span> {reason}
                </div>
                <p className="text-[11px] text-gray-500">
                  This action is logged with your identity and IP. Reversible within 24 hours from the history table.
                </p>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setConfirmOpen(false)} disabled={submitting}>Cancel</Button>
              <Button onClick={submitCredit} disabled={submitting} className="bg-amber-600 hover:bg-amber-700" data-testid="coin-credit-confirm-btn">
                {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Coins className="w-4 h-4 mr-2" />}
                Confirm credit
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Recent credits table */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <CardTitle className="text-base">Credit history</CardTitle>
              <Button variant="outline" size="sm" onClick={loadHistory} disabled={historyLoading}>
                {historyLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Refresh'}
              </Button>
            </div>
            <CardDescription className="text-xs">Every credit + reversal, oldest 24-hour reversal window still eligible.</CardDescription>

            {/* Filters */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-2 mt-3 pt-3 border-t">
              <div>
                <Label className="text-xs text-gray-600">Target INDoS</Label>
                <Input
                  value={filterIndos}
                  onChange={(e) => setFilterIndos(e.target.value.toUpperCase())}
                  placeholder="e.g. 24TP1234"
                  className="h-9 text-sm uppercase"
                  data-testid="coin-credit-filter-indos"
                />
              </div>
              <div>
                <Label className="text-xs text-gray-600">Kind</Label>
                <select
                  value={filterKind}
                  onChange={(e) => setFilterKind(e.target.value)}
                  className="h-9 w-full text-sm border rounded-md px-2 bg-white"
                  data-testid="coin-credit-filter-kind"
                >
                  <option value="all">All</option>
                  <option value="credit">Credits only</option>
                  <option value="reversal">Reversals only</option>
                </select>
              </div>
              <div className="md:col-span-2 flex items-end gap-2">
                <Button size="sm" onClick={applyFilters} className="bg-amber-600 hover:bg-amber-700" data-testid="coin-credit-apply-filters">Apply</Button>
                <Button size="sm" variant="outline" onClick={resetFilters} data-testid="coin-credit-reset-filters">Reset</Button>
                <p className="text-xs text-gray-500 ml-auto">Total: <span className="font-semibold">{historyTotal}</span></p>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {history.length === 0 ? (
              <p className="p-6 text-center text-sm text-gray-400">No credits issued yet.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-gray-600">
                    <tr>
                      <th className="text-left px-3 py-2 font-medium">When</th>
                      <th className="text-left px-3 py-2 font-medium">Admin</th>
                      <th className="text-left px-3 py-2 font-medium">Target</th>
                      <th className="text-right px-3 py-2 font-medium">Amount</th>
                      <th className="text-left px-3 py-2 font-medium">Reason</th>
                      <th className="text-right px-3 py-2 font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {history.map((r) => (
                      <tr key={r.id} className={`border-t ${r.reversed_at ? 'bg-red-50/30' : ''}`} data-testid={`coin-credit-row-${r.id}`}>
                        <td className="px-3 py-2 text-xs text-gray-700 whitespace-nowrap">
                          {new Date(r.created_at).toLocaleString()}
                        </td>
                        <td className="px-3 py-2">
                          <div className="text-gray-900">{r.admin_name}</div>
                          <div className="text-[10px] text-gray-400">{r.admin_email}</div>
                        </td>
                        <td className="px-3 py-2">
                          <div className="text-gray-900">{r.target_user_name}</div>
                          <div className="text-[10px] text-gray-400 font-mono">{r.target_user_indos}</div>
                        </td>
                        <td className={`px-3 py-2 text-right tabular-nums font-semibold ${r.amount < 0 ? 'text-red-600' : 'text-emerald-700'}`}>
                          {r.amount > 0 ? `+${r.amount}` : r.amount}
                        </td>
                        <td className="px-3 py-2 text-gray-700 max-w-xs">
                          <div className="line-clamp-2" title={r.reason}>{r.reason}</div>
                          {r.kind === 'reversal' && (
                            <Badge variant="outline" className="mt-1 text-[10px] bg-red-50 text-red-700 border-red-200">Reversal</Badge>
                          )}
                          {r.reversed_at && (
                            <Badge variant="outline" className="mt-1 ml-1 text-[10px] bg-red-50 text-red-700 border-red-200">Reversed</Badge>
                          )}
                        </td>
                        <td className="px-3 py-2 text-right">
                          {r.kind === 'credit' && !r.reversed_at && withinReversalWindow(r.created_at) && (
                            <Button
                              variant="outline"
                              size="sm"
                              disabled={reversing === r.id}
                              onClick={() => doReverse(r.id)}
                              className="text-xs text-red-700 border-red-200 hover:bg-red-50"
                              data-testid={`coin-credit-reverse-${r.id}`}
                            >
                              {reversing === r.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Undo2 className="w-3 h-3 mr-1" />}
                              Reverse
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Pagination */}
            {historyTotal > HISTORY_PER_PAGE && (
              <div className="flex items-center justify-between px-4 py-3 border-t bg-gray-50">
                <p className="text-xs text-gray-500">
                  Page {historyPage} of {historyTotalPages} · showing {history.length} of {historyTotal}
                </p>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" disabled={historyPage <= 1 || historyLoading} onClick={() => setHistoryPage((p) => Math.max(1, p - 1))} data-testid="coin-credit-prev-btn">Previous</Button>
                  <Button variant="outline" size="sm" disabled={historyPage >= historyTotalPages || historyLoading} onClick={() => setHistoryPage((p) => Math.min(historyTotalPages, p + 1))} data-testid="coin-credit-next-btn">Next</Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default CoinCreditsPage;
